import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Loader2, X, Check } from 'lucide-react';
import { DirectoryItem } from '../../services/mockDirectoryService'; // Interfaces only

interface SearchParams {
  query: string;
  page: number;
  pageSize: number;
  sortBy?: 'name' | 'id';
  sortDir?: 'asc' | 'desc';
}

interface SearchResult {
  items: DirectoryItem[];
  total: number;
  hasMore: boolean;
}

interface AsyncSelectProps {
  label?: string;
  placeholder?: string;
  value?: string; // ID
  onChange: (value: string | undefined, item: DirectoryItem | undefined) => void;
  fetchOptions: (params: SearchParams) => Promise<SearchResult>;
  fetchItem?: (id: string) => Promise<DirectoryItem | undefined>;
  disabled?: boolean;
}

export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  label,
  placeholder = 'Select...',
  value,
  onChange,
  fetchOptions,
  fetchItem,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<DirectoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DirectoryItem | undefined>(undefined);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load initial value
  useEffect(() => {
    if (value && !selectedItem && fetchItem) {
        setLoading(true);
        fetchItem(value)
            .then(item => {
                if (item) setSelectedItem(item);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }
  }, [value, fetchItem]);

  useEffect(() => {
    if (isOpen) {
        loadData(1, query);
    }
  }, [isOpen, query]);

  const loadData = async (pageNum: number, searchQuery: string) => {
    setLoading(true);
    try {
        const res = await fetchOptions({
            query: searchQuery,
            page: pageNum,
            pageSize: 10,
            sortBy: 'name',
            sortDir: 'asc'
        });
        
        if (pageNum === 1) {
            setItems(res.items);
        } else {
            setItems(prev => [...prev, ...res.items]);
        }
        
        setHasMore(res.hasMore);
        setPage(pageNum);
    } catch (err) {
        console.error("AsyncSelect load failed", err);
    } finally {
        setLoading(false);
    }
  };

  const handleSelect = (item: DirectoryItem) => {
      setSelectedItem(item);
      onChange(item.id, item);
      setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedItem(undefined);
      onChange(undefined, undefined);
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            setIsOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Infinite scroll mock
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !loading) {
          loadData(page + 1, query);
      }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
        if (isOpen) loadData(1, query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{label}</label>}
      
      <div 
        className={`
            w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-900 cursor-pointer transition-all
            ${isOpen ? 'border-teal-500 ring-1 ring-teal-500/20' : 'border-slate-300 dark:border-slate-700 hover:border-slate-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-100' : ''}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`truncate ${!selectedItem && !value ? 'text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
            {selectedItem ? selectedItem.name : (value || placeholder)}
        </span>
        <div className="flex items-center gap-1">
            {(selectedItem || value) && !disabled && (
                <div onClick={handleClear} className="p-1 hover:bg-slate-100 rounded-full text-slate-400 hover:text-red-500">
                    <X size={14} />
                </div>
            )}
            <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
            <div className="p-2 border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <input 
                        autoFocus
                        type="text" 
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search..."
                        className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800 border border-transparent focus:bg-white focus:border-teal-500 rounded-md outline-none transition-all"
                    />
                </div>
            </div>
            
            <div 
                ref={listRef}
                className="max-h-[200px] overflow-y-auto p-1"
                onScroll={handleScroll}
            >
                {items.length === 0 && !loading && (
                    <div className="p-4 text-center text-xs text-slate-400">No results found.</div>
                )}

                {items.map(item => (
                    <div 
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        className={`
                            flex items-center gap-3 p-2 rounded-md cursor-pointer group
                            ${item.id === value ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}
                        `}
                    >
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 shrink-0">
                            {item.avatar || item.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate">{item.name}</div>
                            {item.subtitle && <div className="text-[10px] text-slate-400 truncate">{item.subtitle}</div>}
                        </div>
                        {item.id === value && <Check size={14} className="text-teal-600" />}
                    </div>
                ))}
                
                {loading && (
                    <div className="flex justify-center p-2">
                        <Loader2 size={16} className="animate-spin text-teal-500" />
                    </div>
                )}
            </div>
        </div>
      )}
    </div>
  );
};